import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Spin, Tooltip } from "antd";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import { useGetBankQuery } from "../../../redux/banks/bankAPI";
import {
  updateOpenModalControlPanelBank,
  updateControlPanelDrawerPlacement,
  updateBankSelected,
  updateServerId,
  updateChannelId,
} from "../../../redux/monitoring/monitoringSlice";
// COMPONENTS
import MonitoringElevatorsFullView from "../fullView/MonitoringElevatorsFullView";
import MonitoringTopView from "../topView/MonitoringTopView";
import MonitoringEscalatorsFullView from "../fullView/MonitoringEscalatorsFullView";
import MonitoringMovingWalkFullView from "../fullView/MonitoringMovingWalkFullView";
import MonitoringStatusView from "../statusView/MonitoringStatusView";
import MonitoringHeartBeat from "./MonitoringHeartBeat";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// INTERFACES
import { MonitoringViewTypeEnum } from "../../../interfaces/Monitoring.interface";
import { RootState } from "../../../app/store";
import { BankInterface } from "../../../interfaces/Bank.interface";
// CONSTANTS
import { BLANK_BANK } from "../../../constants/bank";
// UTILS
import { emptyFunctionDeclaration } from "../../../utils/utils";

function MonitoringBank({
  bankId,
  serverId,
  channelId,
  h,
  w,
}: {
  bankId: string;
  serverId: string;
  channelId: string;
  h: number;
  w: number;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();
  const SHOW_GRID_INFO = false;

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [token, setToken] = useState<string | null>(null);
  const [bank, setBank] = useState<BankInterface>({ ...BLANK_BANK });

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const monitoringGridType = useSelector(
    (state: RootState) => state.monitoring.monitoringGridType
  );
  const enableDragAndDrop = useSelector(
    (state: RootState) => state.monitoring.enableDragAndDrop
  );
  const theme = useSelector((state: RootState) => state.home.theme);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const { data, isLoading, isFetching } = useGetBankQuery(
    { token, bankId },
    { skip: !token, refetchOnMountOrArgChange: false }
  );

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const getDrawerPlacement = (e: any) => {
    const clickX = e.clientX;
    const screenWidth = window.innerWidth;
    if (clickX > screenWidth / 2) {
      dispatch(updateControlPanelDrawerPlacement("left"));
    } else {
      dispatch(updateControlPanelDrawerPlacement("right"));
    }
  };

  const onClickOpenDrawerBank = (e: any) => {
    getDrawerPlacement(e);
    dispatch(updateBankSelected(bank));
    dispatch(updateServerId(serverId));
    dispatch(updateChannelId(channelId));
    dispatch(updateOpenModalControlPanelBank(true));
  };

  // ************************************************* */
  // USE EFFECT ************************************** */
  useEffect(() => {
    GETJwtToken().then(setToken);
  }, []);

  useEffect(() => {
    if (data && data.data) {
      setBank(data.data);
    }
  }, [data]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div
      className={`monitoring__card__${theme} generalStyles__width100`}
      style={{ height: "auto" }}
    >
      {/** *********************************************************** */}
      {/** HEADER */}
      <div className="monitoring__card__header">
        <div className="generalStyles__flex">
          <MonitoringHeartBeat bankId={bank.id} />
          <div
            className="generalStyles__textEllipsis"
            role="button"
            tabIndex={0}
            onKeyDown={emptyFunctionDeclaration}
            onClick={(e: any) => {
              if (!enableDragAndDrop) {
                onClickOpenDrawerBank(e);
              }
            }}
          >
            <Tooltip
              placement="topLeft"
              title={`${bank.name} ${SHOW_GRID_INFO ? `${h}-${w}` : ""}`}
            >
              <h6>{bank.name}</h6>
            </Tooltip>
          </div>
        </div>
      </div>
      <Spin spinning={isFetching || isLoading}>
        {/* ************************************************* */}
        {/** OVER HEAD VIEW */}
        {monitoringGridType === MonitoringViewTypeEnum.OVER_HEAD_VIEW && (
          <>
            {bank.type === "ELEVATOR" && (
              <MonitoringTopView
                bank={bank}
                serverId={serverId}
                channelId={channelId}
              />
            )}
            {bank.type === "ESCALATOR" && (
              <MonitoringEscalatorsFullView
                bank={bank}
                serverId={serverId}
                channelId={channelId}
                enableActions
              />
            )}
            {bank.type === "MOVINGWALK" && (
              <MonitoringMovingWalkFullView
                bank={bank}
                serverId={serverId}
                channelId={channelId}
              />
            )}
          </>
        )}
        {/* ************************************************* */}
        {/** STATUS VIEW */}
        {monitoringGridType === MonitoringViewTypeEnum.STATUS_VIEW && (
          <MonitoringStatusView
            bank={bank}
            serverId={serverId}
            channelId={channelId}
          />
        )}
        {/* ************************************************* */}
        {/** EXPANDED VIEW */}
        {monitoringGridType === MonitoringViewTypeEnum.EXPANDED_VIEW && (
          <div className="mt-2">
            {bank.type === "ELEVATOR" && (
              <MonitoringElevatorsFullView
                bank={bank}
                serverId={serverId}
                channelId={channelId}
                enableActions
              />
            )}
            {bank.type === "ESCALATOR" && (
              <MonitoringEscalatorsFullView
                bank={bank}
                serverId={serverId}
                channelId={channelId}
                enableActions
              />
            )}
            {bank.type === "MOVINGWALK" && (
              <MonitoringMovingWalkFullView
                bank={bank}
                serverId={serverId}
                channelId={channelId}
              />
            )}
          </div>
        )}
      </Spin>
    </div>
  );
}

export default React.memo(MonitoringBank);
